import { ReactNode, useState, useEffect, useCallback } from 'react';
import MeldingKnop from './MeldingKnop';
import VersieBadge from './VersieBadge';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Bot,
  LayoutDashboard,
  LogOut,
  Mail,
  MessageSquare,
  Menu,
  X,
  AlertTriangle,
  Sun,
  Moon,
  Search,
  Eye,
  Tv,
} from 'lucide-react';
import type { Role } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import ProfileModal from './ProfileModal';
import NotificationBell from './NotificationBell';
import GlobalSearch from './GlobalSearch';
import PwaInstallBanner from './PwaInstallBanner';
import PushOnboardingPrompt from './PushOnboardingPrompt';
import ChatWidget from './ai-chat/ChatWidget';
import IncomingCallPop from './telephony/IncomingCallPop';
import GraphHealthAlert from './GraphHealthAlert';
import CriticalAlertsGate from './CriticalAlertsGate';
import WhatsAppHealthAlert from './WhatsAppHealthAlert';
import { useWhatsAppUnreadCount } from '../hooks/useWhatsAppUnreadCount';
import { useOpenProposalsCount } from '../features/ai-org';
import { onAiFallback } from '../lib/edgeFunctions';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import { useOnthoudVorigeLocatie } from '@/shared/navigatie/vorigeLocatie';
import { useSignedUrl } from '../lib/storageUrl';

interface LayoutProps {
  children: ReactNode;
}

const VIEW_ROLES: Role[] = [
  'Projectleider',
  'Werkvoorbereider',
  'Administratie',
  'Adviseur',
  'Hoofd Adviseur',
  'Secretaresse',
  'Planner',
  'Onderaannemer',
  'OA-medewerker',
  'Transporteur',
];

export default function Layout({ children }: LayoutProps) {
  useScrollRestoration();
  useOnthoudVorigeLocatie();
  const { profile, realProfile, viewAsRole, setViewAsRole, signOut } = useAuth();
  const isRealAdmin = realProfile?.role === 'Admin';
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [showProfile, setShowProfile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { count: whatsAppActionCount } = useWhatsAppUnreadCount();

  const canAccessWhatsApp =
    profile?.role === 'Admin' ||
    profile?.role === 'Werkvoorbereider' ||
    profile?.role === 'Hoofd Adviseur' ||
    profile?.role === 'Projectleider' ||
    profile?.role === 'Secretaresse';
  const isAdmin = profile?.role === 'Admin';
  const { count: openProposalsCount } = useOpenProposalsCount(isAdmin);
  const [fallbackMsg, setFallbackMsg] = useState<string | null>(null);

  const handleFallback = useCallback((msg: string) => {
    setFallbackMsg(msg);
    setTimeout(() => setFallbackMsg(null), 8000);
  }, []);

  useEffect(() => {
    const unsubscribe = onAiFallback(handleFallback);
    return unsubscribe;
  }, [handleFallback]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('body-scroll-lock');
    } else {
      document.body.classList.remove('body-scroll-lock');
    }
    return () => document.body.classList.remove('body-scroll-lock');
  }, [mobileMenuOpen]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // In `profiles.avatar_url` staat een absolute publieke storage-URL; die wordt opnieuw getekend.
  const avatarUrl = useSignedUrl(profile?.avatar_url);

  const initials =
    (profile?.full_name?.trim() || profile?.email || '?')
      .split(' ')
      .filter((w) => w.length > 0)
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || '?';

  return (
    <div className="min-h-screen bg-surface text-foreground">
      <nav className="bg-dark text-white sticky top-0 z-40 border-b border-gray-800">
        <div className="px-3 sm:px-5">
          <div className="flex justify-between h-14">
            <div className="flex items-center gap-2 sm:gap-5">
              <Link to="/" className="flex items-center">
                <img src="/asset_23_kopie.png" alt="Logo" className="h-8 w-auto object-contain" />
              </Link>

              <div className="hidden md:flex items-center gap-1">
                <Link
                  to="/"
                  className={`flex items-center gap-2 whitespace-nowrap px-3 py-1.5 rounded-lg text-[13.5px] font-medium transition-colors ${
                    isActive('/')
                      ? 'bg-gray-800 text-white shadow-[0_0_12px_rgba(245,152,175,0.15)]'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <LayoutDashboard className="w-4 h-4" />
                  <span>Dashboard</span>
                </Link>
                {(profile?.role === 'Admin' ||
                  profile?.role === 'Werkvoorbereider' ||
                  profile?.role === 'Hoofd Adviseur' ||
                  profile?.role === 'Projectleider') && (
                  <Link
                    to="/notifications"
                    className={`flex items-center gap-2 whitespace-nowrap px-3 py-1.5 rounded-lg text-[13.5px] font-medium transition-colors ${
                      isActive('/notifications')
                        ? 'bg-gray-800 text-white shadow-[0_0_12px_rgba(245,152,175,0.15)]'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    <Mail className="w-4 h-4" />
                    <span>Klant Notificaties</span>
                  </Link>
                )}
                {canAccessWhatsApp && (
                  <Link
                    to="/whatsapp"
                    className={`relative flex items-center gap-2 whitespace-nowrap px-3 py-1.5 rounded-lg text-[13.5px] font-medium transition-colors ${
                      isActive('/whatsapp')
                        ? 'bg-gray-800 text-white shadow-[0_0_12px_rgba(245,152,175,0.15)]'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    <MessageSquare className="w-4 h-4" />
                    <span>WhatsApp</span>
                    {whatsAppActionCount > 0 && (
                      <span className="absolute -right-1 -top-1 min-w-[18px] rounded-full bg-[#25D366] px-1 text-center text-[10px] font-bold leading-[18px] text-white">
                        {whatsAppActionCount > 99 ? '99+' : whatsAppActionCount}
                      </span>
                    )}
                  </Link>
                )}
                {isAdmin && (
                  <Link
                    to="/ai-organisatie"
                    className={`relative flex items-center gap-2 whitespace-nowrap px-3 py-1.5 rounded-lg text-[13.5px] font-medium transition-colors ${
                      location.pathname.startsWith('/ai-organisatie') || isActive('/chief')
                        ? 'bg-gray-800 text-white shadow-[0_0_12px_rgba(245,152,175,0.15)]'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    <Bot className="w-4 h-4" />
                    <span>AI Organisatie</span>
                    {openProposalsCount > 0 && (
                      <span className="absolute -right-1 -top-1 min-w-[18px] rounded-full bg-brand px-1 text-center text-[10px] font-bold leading-[18px] text-white">
                        {openProposalsCount > 99 ? '99+' : openProposalsCount}
                      </span>
                    )}
                  </Link>
                )}
                {isAdmin && (
                  <Link
                    to="/mission-control"
                    className={`flex items-center gap-2 whitespace-nowrap px-3 py-1.5 rounded-lg text-[13.5px] font-medium transition-colors ${
                      isActive('/mission-control')
                        ? 'bg-gray-800 text-white shadow-[0_0_12px_rgba(245,152,175,0.15)]'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    <Tv className="w-4 h-4" />
                    <span>Mission Control</span>
                  </Link>
                )}
                {profile?.role === 'Transporteur' && null}
              </div>
            </div>

            <div className="flex items-center gap-1 sm:gap-2">
              {isRealAdmin && (
                <div
                  className="hidden lg:flex h-9 items-center gap-1.5 rounded-full border border-gray-700/80 bg-gray-900 px-3"
                  title="Bekijk de app als een andere rol (alleen weergave — je blijft Admin)"
                >
                  <Eye className="h-4 w-4 text-gray-400" />
                  <select
                    value={viewAsRole || ''}
                    onChange={(e) => setViewAsRole((e.target.value || null) as Role | null)}
                    className="bg-transparent text-xs font-medium text-gray-200 outline-none cursor-pointer [&>option]:bg-gray-900"
                    aria-label="Bekijk als rol"
                  >
                    <option value="">Bekijk als…</option>
                    {VIEW_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <button
                onClick={() => setSearchOpen(true)}
                className="p-2 rounded-lg hover:bg-gray-800 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Zoeken"
                title="Zoeken (⌘K)"
              >
                <Search className="w-5 h-5" />
              </button>
              <NotificationBell />
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-gray-800 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label={
                  theme === 'light' ? 'Schakel naar donkere modus' : 'Schakel naar lichte modus'
                }
                title={theme === 'light' ? 'Donkere modus' : 'Lichte modus'}
              >
                {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setShowProfile(true)}
                className="flex items-center space-x-2 sm:space-x-3 px-2 sm:px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer min-h-[44px]"
              >
                {profile?.avatar_url ? (
                  <img
                    src={avatarUrl ?? profile.avatar_url}
                    alt={profile.full_name || 'Profiel'}
                    className="w-8 h-8 rounded-full object-cover ring-2 ring-brand/50"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center ring-2 ring-brand/50">
                    <span className="text-xs font-medium text-gray-200">{initials}</span>
                  </div>
                )}
                <div className="hidden items-baseline gap-1.5 text-left sm:flex">
                  <span className="whitespace-nowrap text-sm font-medium">
                    {profile?.full_name || profile?.email}
                  </span>
                  <span className="hidden whitespace-nowrap text-xs text-gray-400 xl:inline">
                    · {profile?.role}
                  </span>
                </div>
              </button>
              {!['Onderaannemer', 'OA-medewerker', 'Transporteur'].includes(
                profile?.role ?? '',
              ) && <MeldingKnop />}
              <button
                onClick={handleSignOut}
                title="Uitloggen"
                aria-label="Uitloggen"
                className="hidden min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2 text-gray-300 transition-colors hover:bg-gray-800 hover:text-white md:flex"
              >
                <LogOut className="h-5 w-5" />
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-md text-gray-300 hover:bg-gray-800 hover:text-white transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label={mobileMenuOpen ? 'Sluit menu' : 'Open menu'}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <>
            <div
              className="fixed inset-0 bg-black/50 z-40 md:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            <div className="md:hidden border-t border-gray-700 animate-slide-up relative z-50 bg-black">
              <div className="px-4 pt-2 pb-3 space-y-1 safe-bottom">
                <Link
                  to="/"
                  className={`flex items-center space-x-3 px-3 py-3 rounded-md text-base font-medium transition-colors ${
                    isActive('/')
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <LayoutDashboard className="w-5 h-5" />
                  <span>Dashboard</span>
                </Link>
                {(profile?.role === 'Admin' ||
                  profile?.role === 'Werkvoorbereider' ||
                  profile?.role === 'Hoofd Adviseur' ||
                  profile?.role === 'Projectleider') && (
                  <Link
                    to="/notifications"
                    className={`flex items-center space-x-3 px-3 py-3 rounded-md text-base font-medium transition-colors ${
                      isActive('/notifications')
                        ? 'bg-gray-800 text-white'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    <Mail className="w-5 h-5" />
                    <span>Klant Notificaties</span>
                  </Link>
                )}
                {canAccessWhatsApp && (
                  <Link
                    to="/whatsapp"
                    className={`relative flex items-center space-x-3 px-3 py-3 rounded-md text-base font-medium transition-colors ${
                      isActive('/whatsapp')
                        ? 'bg-gray-800 text-white'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    <MessageSquare className="w-5 h-5" />
                    <span>WhatsApp</span>
                    {whatsAppActionCount > 0 && (
                      <span className="ml-auto min-w-[20px] rounded-full bg-[#25D366] px-1.5 text-center text-xs font-bold leading-5 text-white">
                        {whatsAppActionCount > 99 ? '99+' : whatsAppActionCount}
                      </span>
                    )}
                  </Link>
                )}
                {isAdmin && (
                  <Link
                    to="/ai-organisatie"
                    className={`relative flex items-center space-x-3 px-3 py-3 rounded-md text-base font-medium transition-colors ${
                      location.pathname.startsWith('/ai-organisatie') || isActive('/chief')
                        ? 'bg-gray-800 text-white'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    <Bot className="w-5 h-5" />
                    <span>AI Organisatie</span>
                    {openProposalsCount > 0 && (
                      <span className="ml-auto min-w-[20px] rounded-full bg-brand px-1.5 text-center text-xs font-bold leading-5 text-white">
                        {openProposalsCount > 99 ? '99+' : openProposalsCount}
                      </span>
                    )}
                  </Link>
                )}
                {isAdmin && (
                  <Link
                    to="/mission-control"
                    className={`flex items-center space-x-3 px-3 py-3 rounded-md text-base font-medium transition-colors ${
                      isActive('/mission-control')
                        ? 'bg-gray-800 text-white'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    <Tv className="w-5 h-5" />
                    <span>Mission Control</span>
                  </Link>
                )}
                {isRealAdmin && (
                  <div className="flex items-center gap-3 rounded-md px-3 py-3">
                    <Eye className="h-5 w-5 text-gray-400" />
                    <select
                      value={viewAsRole || ''}
                      onChange={(e) => {
                        setViewAsRole((e.target.value || null) as Role | null);
                        setMobileMenuOpen(false);
                      }}
                      className="flex-1 rounded-lg border border-gray-700 bg-gray-800 px-2 py-2 text-base font-medium text-gray-200 outline-none"
                      aria-label="Bekijk als rol"
                    >
                      <option value="">Bekijk als…</option>
                      {VIEW_ROLES.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <button
                  onClick={() => {
                    handleSignOut();
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center space-x-3 w-full px-3 py-3 rounded-md text-base font-medium text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Uitloggen</span>
                </button>
              </div>
            </div>
          </>
        )}
      </nav>
      <div className="h-px bg-gradient-to-r from-transparent via-brand/40 to-transparent" />
      {viewAsRole && (
        <div className="sticky top-14 z-30 bg-amber-400 text-black sm:top-16">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2 px-4 py-2 sm:px-6 lg:px-8">
            <span className="flex items-center gap-2 text-sm font-medium">
              <Eye className="h-4 w-4 shrink-0" />
              Je bekijkt de app als <strong>{viewAsRole}</strong> — alleen weergave, je blijft
              Admin.
            </span>
            <button
              onClick={() => setViewAsRole(null)}
              className="rounded-lg bg-black px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-gray-800"
            >
              Terug naar Admin
            </button>
          </div>
        </div>
      )}
      <GraphHealthAlert />
      <WhatsAppHealthAlert />
      <CriticalAlertsGate />
      <PwaInstallBanner />

      <main
        className={
          location.pathname === '/'
            ? 'max-lg:px-3 max-lg:py-3 sm:max-lg:px-6 sm:max-lg:py-8'
            : 'max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-8'
        }
      >
        {children}
      </main>

      <PushOnboardingPrompt />
      <VersieBadge />
      <IncomingCallPop />
      <ChatWidget />
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      {showProfile && <ProfileModal onClose={() => setShowProfile(false)} />}

      {fallbackMsg && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 z-50 sm:max-w-sm animate-slide-up safe-bottom">
          <div className="bg-amber-50 border border-amber-300 rounded-lg shadow-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">AI Fallback actief</p>
              <p className="text-xs text-amber-700 mt-1">{fallbackMsg}</p>
            </div>
            <button
              onClick={() => setFallbackMsg(null)}
              className="text-amber-500 hover:text-amber-700 flex-shrink-0"
              aria-label="Sluit AI fallback melding"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
